/**
 * useMCPBrowser Hook
 *
 * Hook for managing MCP tool browser state
 * Migrated from tool-browser.js createMcp() function
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useAvailableMCPServers } from './useTools';
import type {
  ToolServerType,
  MCPServerGroup,
  MCPServersConfig,
} from '../../../types/tools';

interface UseMCPBrowserOptions {
  initialConfig?: MCPServersConfig | string;
  onApply?: (config: string) => void;
}

export function useMCPBrowser({ initialConfig, onApply }: UseMCPBrowserOptions) {
  // State
  const [activeTab, setActiveTab] = useState<ToolServerType>('system');
  const [selectedServers, setSelectedServers] = useState<string[]>([]);
  const [enabledMcpTools, setEnabledMcpTools] = useState<string[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch MCP servers
  const {
    data: mcpData,
    isLoading,
    error,
    refresh,
  } = useAvailableMCPServers();

  // Parse initial configuration
  useEffect(() => {
    if (!initialConfig) return;

    try {
      const parsed =
        typeof initialConfig === 'string' ? JSON.parse(initialConfig) : initialConfig;

      if (typeof parsed === 'string') {
        setSelectedServers([parsed]);
      } else if (Array.isArray(parsed)) {
        setSelectedServers(parsed);
      } else if (parsed && typeof parsed === 'object') {
        setSelectedServers(parsed.servers || []);
        setEnabledMcpTools(parsed.enabled_tools || []);
      }
    } catch (e) {
      console.warn('Could not parse initial MCP config:', e);
    }
  }, [initialConfig]);

  // Computed values
  const mcpTools = mcpData?.mcp_tools || [];
  const servers = mcpData?.servers || { system: [], user: [] };
  const status = mcpData?.status || 'success';
  const warnings = mcpData?.warnings || [];
  const failedServers = mcpData?.failed_servers || [];
  const staleServers = mcpData?.stale_servers || [];

  // Check if connections need refresh (stale connections detected)
  const needsRefresh = status === 'stale' || staleServers.length > 0;

  const selectedToolCount = enabledMcpTools.length;

  // Build set of all available server IDs
  const availableServerIds = useMemo(() => {
    const ids = new Set<string>();
    [...(servers.system || []), ...(servers.user || [])].forEach((id) =>
      ids.add(id)
    );
    return ids;
  }, [servers]);

  // Build set of all available tool names
  const availableToolNames = useMemo(() => {
    const names = new Set<string>();
    mcpTools.forEach((tool) => {
      names.add(tool.namespaced_name || tool.name);
    });
    return names;
  }, [mcpTools]);

  // Detect invalid servers and tools (selected but don't exist in available list)
  const invalidServers = useMemo(() => {
    const invalid = new Set<string>();
    selectedServers.forEach((serverId) => {
      if (!availableServerIds.has(serverId)) {
        invalid.add(serverId);
      }
    });
    return invalid;
  }, [selectedServers, availableServerIds]);

  const invalidTools = useMemo(() => {
    const invalid = new Set<string>();
    enabledMcpTools.forEach((toolName) => {
      if (!availableToolNames.has(toolName)) {
        invalid.add(toolName);
      }
    });
    return invalid;
  }, [enabledMcpTools, availableToolNames]);

  const hasInvalidItems = invalidServers.size > 0 || invalidTools.size > 0;

  // Group MCP tools by server for display
  const groupedMcpTools = useMemo((): MCPServerGroup[] => {
    const serverIds = servers[activeTab] || [];

    return serverIds.map((serverId) => {
      const serverTools = mcpTools.filter((t) => t.server_id === serverId);
      if (serverTools.length === 0) {
        return {
          server_id: serverId,
          server_type: activeTab,
          connected: false,
          transport: 'stdio',
          tools: [],
          tool_count: 0,
        };
      }
      return {
        server_id: serverId,
        server_type: activeTab,
        connected: serverTools[0].connected,
        transport: serverTools[0].transport,
        tools: serverTools,
        tool_count: serverTools.length,
      };
    });
  }, [mcpTools, servers, activeTab]);

  // Generate configuration JSON
  const generatedMcpConfig = useMemo(() => {
    if (selectedServers.length === 0) {
      return '';
    }

    const config: MCPServersConfig = {
      servers: selectedServers,
    };

    if (enabledMcpTools.length > 0) {
      config.enabled_tools = enabledMcpTools;
    }

    return JSON.stringify(config, null, 2);
  }, [selectedServers, enabledMcpTools]);

  // Find server by ID
  const findServer = useCallback(
    (serverId: string): MCPServerGroup | null => {
      const serverTools = mcpTools.filter((t) => t.server_id === serverId);
      if (serverTools.length === 0) {
        return null;
      }
      return {
        server_id: serverId,
        server_type: serverTools[0].server_type,
        connected: serverTools[0].connected,
        transport: serverTools[0].transport,
        tools: serverTools,
        tool_count: serverTools.length,
      };
    },
    [mcpTools]
  );

  // Actions
  const toggleServer = useCallback(
    (serverId: string) => {
      const index = selectedServers.indexOf(serverId);
      if (index > -1) {
        // Remove server and all its tools
        setSelectedServers((prev) => prev.filter((id) => id !== serverId));
        const server = findServer(serverId);
        if (server && server.tools) {
          setEnabledMcpTools((prev) =>
            prev.filter(
              (toolName) =>
                !server.tools.some(
                  (t) => (t.namespaced_name || t.name) === toolName
                )
            )
          );
        }
      } else {
        // Add server and auto-enable all its tools
        setSelectedServers((prev) => [...prev, serverId]);
        const server = findServer(serverId);
        if (server && server.tools) {
          setEnabledMcpTools((prev) => {
            const newTools = server.tools
              .map((t) => t.namespaced_name || t.name)
              .filter((name) => !prev.includes(name));
            return [...prev, ...newTools];
          });
        }
      }
    },
    [selectedServers, findServer]
  );

  const toggleMcpTool = useCallback(
    (toolName: string, serverId: string) => {
      const index = enabledMcpTools.indexOf(toolName);
      if (index > -1) {
        // Remove tool
        setEnabledMcpTools((prev) => prev.filter((name) => name !== toolName));
        // Auto-deselect server if no tools from it are selected
        if (selectedServers.includes(serverId)) {
          const serverTools = mcpTools.filter((t) => t.server_id === serverId);
          const hasSelectedTools = serverTools.some((t) =>
            enabledMcpTools.filter((name) => name !== toolName).includes(
              t.namespaced_name || t.name
            )
          );
          if (!hasSelectedTools) {
            setSelectedServers((prev) => prev.filter((id) => id !== serverId));
          }
        }
      } else {
        // Add tool
        setEnabledMcpTools((prev) => [...prev, toolName]);
        // Auto-select server when a tool is selected
        if (!selectedServers.includes(serverId)) {
          setSelectedServers((prev) => [...prev, serverId]);
        }
      }
    },
    [enabledMcpTools, selectedServers, mcpTools]
  );

  const isServerSelected = useCallback(
    (serverId: string) => selectedServers.includes(serverId),
    [selectedServers]
  );

  const isMcpToolEnabled = useCallback(
    (toolName: string) => enabledMcpTools.includes(toolName),
    [enabledMcpTools]
  );

  const refreshMcpConnections = useCallback(async () => {
    console.log('Refreshing MCP connections...');
    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      setIsRefreshing(false);
    }
  }, [refresh]);

  const handleReopen = useCallback(
    (config?: MCPServersConfig | string) => {
      console.log('MCP tool browser reopened, refreshing server list...');

      // Clear previous selections first
      setSelectedServers([]);
      setEnabledMcpTools([]);

      // Parse the current config to restore selections
      if (config) {
        try {
          const parsed = typeof config === 'string' ? JSON.parse(config) : config;

          if (typeof parsed === 'string') {
            setSelectedServers([parsed]);
          } else if (Array.isArray(parsed)) {
            setSelectedServers(parsed);
          } else if (parsed && typeof parsed === 'object') {
            setSelectedServers(parsed.servers || []);
            setEnabledMcpTools(parsed.enabled_tools || []);
          }
        } catch (e) {
          console.warn('Could not parse config on reopen:', e);
        }
      }

      // Reload server list from API to get latest data
      refresh();
    },
    [refresh]
  );

  const handleApply = useCallback(() => {
    if (onApply) {
      onApply(generatedMcpConfig);
    }
  }, [generatedMcpConfig, onApply]);

  return {
    // State
    activeTab,
    setActiveTab,
    selectedServers,
    enabledMcpTools,

    // Data
    mcpTools,
    servers,
    groupedMcpTools,
    status,
    warnings,
    failedServers,
    staleServers,

    // Computed
    selectedToolCount,
    generatedMcpConfig,
    needsRefresh,

    // Validation
    invalidServers,
    invalidTools,
    hasInvalidItems,

    // Loading/Error
    isLoading: isLoading || isRefreshing,
    isRefreshing,
    error: error ? String(error) : null,

    // Actions
    toggleServer,
    toggleMcpTool,
    isServerSelected,
    isMcpToolEnabled,
    refreshMcpConnections,
    handleReopen,
    handleApply,
  };
}
